import BaseDialog from '@/components/Dialogs/BaseDialog';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import ContainerIconSVG from '@/images/deploy/container.svg';
import ContentCopySvg from '@/images/deploy/content_copy.svg';
import { IconButton, Typography } from '@clef/client-library';
import { useTypographyStyles } from '@clef/client-library/src/themes/typography';
import { Box, DialogProps, Link, makeStyles } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import React, { useState } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { lightfair } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { EnvironmentEnum } from '../../../constants';
import ArchSelector from './DockerDeployment/ArchSelector';
import OSOption, { getCurrentOS } from './DockerDeployment/OSOption';
export interface DeployModelRunnerProps extends DialogProps {
  open: boolean;
  onClose: () => void | Promise<void>;
  openModelLink: () => void;
}

const useStyles = makeStyles(theme => ({
  linkButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  linkButtonIcon: {
    marginLeft: theme.spacing(1),
    fontSize: 16,
  },
  linkButtonLabel: {
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  copyApiCommandButton: {
    background: theme.palette.common.white,
    position: 'absolute',
    bottom: 10,
    right: 10,
    borderRadius: 6,
    width: 30,
    height: 30,
  },
  tabLabel: {
    fontSize: 14,
  },
  leftTitle: {
    display: 'flex',
    alignItems: 'center',
    flex: '1',
    maxWidth: '85px',
  },
}));

const getDockerImage = (osType: OSOption): any => {
  switch (osType) {
    case OSOption.JetsonJetpack4x: {
      return {
        imageRepo: 'public.ecr.aws/landing-ai/deploy:latest-jp4',
      };
    }
    case OSOption.JetsonJetpack5x: {
      return {
        imageRepo: 'public.ecr.aws/landing-ai/deploy:latest-jp5',
      };
    }
    default: {
      return {
        imageRepo: 'public.ecr.aws/landing-ai/deploy:latest',
      };
    }
  }
};

interface DeployCommandProps {
  osType: OSOption;
}

const DeployCommand: React.FC<DeployCommandProps> = ({ osType }) => {
  const styles = useStyles();
  const { imageRepo } = getDockerImage(osType);
  const getInfoTypeString = (option: OSOption): { [key: string]: string } => {
    switch (option) {
      case OSOption.Windows:
        return {
          docker: `docker run -p 8000:8000 --rm \`
  -e SNOWFLAKE_ACCOUNT=<account identifier> \`
  -e SNOWFLAKE_APP_URL=<app url> \`
  -e SNOWFLAKE_USER=<username> \`
  -e USE_SNOWFLAKE=1 \`
  -e SNOWFLAKE_PRIVATE_KEY_PATH="/path/to/key.p8" \`
  -v /local/path/to:/path/to \`
  ${imageRepo} \`
  run-model-id -m YOUR_MODEL_ID`,
          command: `curl.exe -X POST \`
  -H "Content-Type: multipart/form-data" \`
  -F "file=@./input_image.jpg" \`
  http://localhost:8000/images`,
        };
      case OSOption.JetsonJetpack4x:
        return {
          docker: `docker run -p 8000:8000 --rm \\
  -e SNOWFLAKE_ACCOUNT=<account identifier> \\
  -e SNOWFLAKE_APP_URL=<app url> \\
  -e SNOWFLAKE_USER=<username> \\
  -e USE_SNOWFLAKE=1 \\
  -e SNOWFLAKE_PRIVATE_KEY_PATH="/path/to/key.p8" \\
  -v /local/path/to:/path/to \\
  ${imageRepo} \\
  run-model-id -m YOUR_MODEL_ID`,
          command: `curl -X 'POST' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'file=@./input_image.jpg' \\
  'http://localhost:8000/images'`,
        };
      case OSOption.JetsonJetpack5x:
        return {
          docker: `docker run -p 8000:8000 --rm \\
  -e SNOWFLAKE_ACCOUNT=<account identifier> \\
  -e SNOWFLAKE_APP_URL=<app url> \\
  -e SNOWFLAKE_USER=<username> \\
  -e USE_SNOWFLAKE=1 \\
  -e SNOWFLAKE_PRIVATE_KEY_PATH="/path/to/key.p8" \\
  -v /local/path/to:/path/to \\
  ${imageRepo} \\
  run-model-id -m YOUR_MODEL_ID`,
          command: `curl -X 'POST' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'file=@./input_image.jpg' \\
  'http://localhost:8000/images'`,
        };
      default:
        return {
          docker: `docker run -p 8000:8000 --rm \\
  -e SNOWFLAKE_ACCOUNT=<account identifier> \\
  -e SNOWFLAKE_APP_URL=<app url> \\
  -e SNOWFLAKE_USER=<username> \\
  -e USE_SNOWFLAKE=1 \\
  -e SNOWFLAKE_PRIVATE_KEY_PATH="/path/to/key.p8" \\
  -v /local/path/to:/path/to \\
  ${imageRepo} \\
  run-model-id -m YOUR_MODEL_ID`,
          command: `curl -X 'POST' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'file=@./input_image.jpg' \\
  'http://localhost:8000/images'`,
        };
    }
  };

  const infoTypeString = getInfoTypeString(osType);
  const dockerText = infoTypeString.docker;
  const commandText = infoTypeString.command;

  // Call the useCopyToClipboard hook within the component.
  const handleCopyDocker = useCopyToClipboard({
    text: dockerText,
    successMessage: t('command copied to clipboard.'),
  });

  const handleCopyCommand = useCopyToClipboard({
    text: commandText,
    successMessage: t('command copied to clipboard.'),
  });

  return (
    <Box>
      <Box position="relative" style={{ marginTop: '12px' }}>
        {(EnvironmentEnum.RegulatedProduction || EnvironmentEnum.Local) && (
          <Link
            style={{ cursor: 'pointer' }}
            onClick={() => window.open('https://gallery.ecr.aws/landing-ai/deploy')}
          >
            https://gallery.ecr.aws/landing-ai/deploy
          </Link>
        )}
      </Box>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex' }}>
          <Box className={styles.leftTitle}>
            <Typography>{t('Run')}:</Typography>
          </Box>
          <Box style={{ flex: '1' }}>
            <Box position="relative">
              <SyntaxHighlighter
                wrapLongLines
                lineNumberStyle={{ display: 'none' }}
                language="bash"
                style={lightfair}
                customStyle={{
                  background: 'rgba(188, 198, 212, 0.3)',
                  width: '100%',
                  //   minHeight: '105px',
                }}
              >
                {dockerText}
              </SyntaxHighlighter>
              <IconButton className={styles.copyApiCommandButton} onClick={handleCopyDocker}>
                <img src={ContentCopySvg} width={18} height={18} />
              </IconButton>
            </Box>
          </Box>
        </div>

        <div style={{ display: 'flex' }}>
          <Box className={styles.leftTitle}>
            <Typography>{t('Try')}:</Typography>
          </Box>
          <Box style={{ flex: '1' }}>
            <Box position="relative">
              <SyntaxHighlighter
                wrapLongLines
                lineNumberStyle={{ display: 'none' }}
                language="bash"
                style={lightfair}
                customStyle={{
                  background: 'rgba(188, 198, 212, 0.3)',
                  width: '100%',
                }}
              >
                {commandText}
              </SyntaxHighlighter>
              <IconButton className={styles.copyApiCommandButton} onClick={handleCopyCommand}>
                <img src={ContentCopySvg} width={18} height={18} />
              </IconButton>
            </Box>
          </Box>
        </div>
      </div>
    </Box>
  );
};

const DeployModelRunnerForSnowflake: React.FC<DeployModelRunnerProps> = ({
  open,
  onClose,
  openModelLink,
}) => {
  const typographyStyles = useTypographyStyles();

  const styles = useStyles();
  const [selectedOS, setSelectedOS] = useState<OSOption>(getCurrentOS());

  const onSelect = (value: string) => {
    setSelectedOS(OSOption[value as keyof typeof OSOption]);
  };
  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      containerProps={{ minWidth: 650, paddingX: '30px', paddingY: '30px' }}
      maxWidth="md"
    >
      <Box display="flex" justifyContent="center">
        <img src={ContainerIconSVG} width={100} />
      </Box>
      <Box
        textAlign="center"
        className={typographyStyles.h3_semibold}
        marginBottom={7}
        style={{ marginBottom: '12px' }}
      >
        {t('Container Deploy')}
      </Box>
      <ArchSelector onSelect={onSelect} />
      <DeployCommand osType={selectedOS} />
      <Box display="flex" justifyContent="space-between" width="100%" marginTop={2}>
        <Box>
          <Link className={styles.linkButton} onClick={openModelLink}>
            <Typography className={styles.linkButtonLabel}>{t('Get Model ID')}</Typography>
            <LaunchIcon className={styles.linkButtonIcon} />
          </Link>
        </Box>
        <Box>
          <Link
            className={styles.linkButton}
            style={{
              marginLeft: 'auto',
            }}
            onClick={() => window.open('https://landing-ai.github.io/landingai-python/')}
          >
            <Typography className={styles.linkButtonLabel}>
              {t('See Examples in Our SDK')}
            </Typography>
            <LaunchIcon className={styles.linkButtonIcon} />
          </Link>
        </Box>
      </Box>
    </BaseDialog>
  );
};

export default DeployModelRunnerForSnowflake;
